import React, {useCallback, useEffect, useMemo, useState} from 'react';
import FileSaver from 'file-saver';

import {API_URL} from '../../../api/constants_api';
import grandLoungeLogo from '../../../assets/img/technology/grand-lounge-white.png';
import loungeBackground from '../../../assets/img/technology/public-card-lounge.jpg';
import avatar from '../../../assets/img/faces/face-0.jpg';

const getTokenFromPath = () => {
    const pathParts = window.location.pathname
        .split('/')
        .filter(Boolean);

    return pathParts[0] === 'card'
        ? pathParts[1]
        : null;
};

const normalizePhoneForLink = value =>
    String(value || '').replace(/[^\d+]/g, '');

const normalizePhoneForWhatsApp = value =>
    String(value || '').replace(/\D/g, '');

const escapeVCardValue = value => String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

const blobToBase64 = blob => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
        const result = String(reader.result || '');

        resolve(
            result.includes(',')
                ? result.substring(result.indexOf(',') + 1)
                : result
        );
    };

    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const buildFileUrl = relativeUrl => {
    if (!relativeUrl) {
        return avatar;
    }

    if (
        relativeUrl.startsWith('http://')
        || relativeUrl.startsWith('https://')
    ) {
        return relativeUrl;
    }

    return `${API_URL}${relativeUrl}`;
};

const PublicCardPage = () => {
    const token = useMemo(getTokenFromPath, []);

    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!token) {
            setNotFound(true);
            setLoading(false);

            return;
        }

        fetch(`${API_URL}/public/card/${token}`, {
            method: 'GET',
            headers: {
                Accept: 'application/json'
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Tarjeta no disponible');
                }

                return response.json();
            })
            .then(response => {
                setCard(response.data);
                setLoading(false);
            })
            .catch(() => {
                setNotFound(true);
                setLoading(false);
            });
    }, [token]);

    const contacts = card && Array.isArray(card.contacts)
        ? card.contacts
        : [];

    const emails = contacts.filter(
        contact => contact.type === 'EMAIL'
    );

    const phones = contacts.filter(
        contact => contact.type === 'PHONE'
    );

    const primaryEmail = emails.find(
        contact => contact.isPrimary
    ) || emails[0];

    const primaryPhone = phones.find(
        contact => contact.isPrimary
    ) || phones[0];

    const downloadVCard = useCallback(async () => {
        if (!card || downloading) {
            return;
        }

        setDownloading(true);

        const lines = [
            'BEGIN:VCARD',
            'VERSION:3.0',
            `FN:${escapeVCardValue(card.fullName)}`,
            `ORG:${escapeVCardValue(
                card.corporateIdentity.companyName
                    || 'The Grand Lounge Elite'
            )}`
        ];

        if (card.position) {
            lines.push(
                `TITLE:${escapeVCardValue(card.position)}`
            );
        }

        if (card.area) {
            lines.push(
                `X-DEPARTMENT:${escapeVCardValue(card.area)}`
            );
        }

        emails.forEach(contact => {
            lines.push(
                `EMAIL;TYPE=INTERNET,WORK:${escapeVCardValue(
                    contact.value
                )}`
            );
        });

        phones.forEach(contact => {
            lines.push(
                `TEL;TYPE=CELL,WORK:${escapeVCardValue(
                    contact.value
                )}`
            );
        });

        if (card.corporateIdentity.website) {
            lines.push(
                `URL;TYPE=WORK:${escapeVCardValue(
                    card.corporateIdentity.website
                )}`
            );
        }

        try {
            if (card.photoUrl) {
                const photoResponse = await fetch(
                    buildFileUrl(card.photoUrl)
                );

                if (photoResponse.ok) {
                    const photoBlob = await photoResponse.blob();
                    const photoBase64 = await blobToBase64(photoBlob);
                    const photoType = photoBlob.type === 'image/png'
                        ? 'PNG'
                        : 'JPEG';

                    lines.push(
                        `PHOTO;ENCODING=b;TYPE=${photoType}:${photoBase64}`
                    );
                }
            }
        } catch (error) {
            console.warn(
                'La vCard se generará sin fotografía.',
                error
            );
        }

        lines.push('END:VCARD');

        const vCardContent = `${lines.join('\r\n')}\r\n`;

        const blob = new Blob(
            [vCardContent],
            {
                type: 'text/vcard;charset=utf-8'
            }
        );

        const filename = String(card.fullName || 'contacto')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

        FileSaver.saveAs(
            blob,
            `${filename || 'contacto'}.vcf`
        );

        setDownloading(false);
    }, [
        card,
        downloading,
        emails,
        phones
    ]);

    if (loading) {
        return (
            <PublicCardStatus
                icon="fa fa-spinner fa-spin"
                title="Cargando tarjeta"
                message="Estamos preparando la información de contacto."
            />
        );
    }

    if (notFound || !card) {
        return (
            <PublicCardStatus
                icon="fa fa-id-card-o"
                title="Tarjeta no disponible"
                message={
                    'La tarjeta no existe, fue deshabilitada '
                    + 'o el colaborador ya no se encuentra activo.'
                }
            />
        );
    }

    const identity = card.corporateIdentity || {};

    const socialLinks = [
        {
            key: 'website',
            icon: 'fa fa-globe',
            label: 'Sitio web',
            value: identity.website
        },
        {
            key: 'facebook',
            icon: 'fa fa-facebook',
            label: 'Facebook',
            value: identity.facebook
        },
        {
            key: 'instagram',
            icon: 'fa fa-instagram',
            label: 'Instagram',
            value: identity.instagram
        },
        {
            key: 'tiktok',
            icon: 'fa fa-music',
            label: 'TikTok',
            value: identity.tiktok
        },
        {
            key: 'linkedin',
            icon: 'fa fa-linkedin',
            label: 'LinkedIn',
            value: identity.linkedin
        },
        {
            key: 'youtube',
            icon: 'fa fa-youtube-play',
            label: 'YouTube',
            value: identity.youtube
        }
    ].filter(link => link.value);

    return (
        <div style={styles.page}>
            <div style={styles.card}>

<div style={styles.heroHeader}>

    <div style={styles.heroBackground}>

        <img
            src={grandLoungeLogo}
            alt="The Grand Lounge Elite"
            style={styles.heroLogo}
        />

    </div>

</div>

<div style={styles.identitySection}>

    <img
        src={buildFileUrl(card.photoUrl)}
        alt={card.fullName}
        style={styles.heroPhoto}
    />

    <h1 style={styles.name}>
        {card.fullName}
    </h1>

    <p style={styles.position}>
        {card.position || 'Colaborador'}
    </p>

    {
        card.area &&
        (
            <p style={styles.area}>
                {card.area}
            </p>
        )
    }

    <p style={styles.company}>
        {
            identity.companyName
            || 'The Grand Lounge Elite'
        }
    </p>

</div>

<div style={styles.body}>


                    <div style={styles.primaryActions}>
                        {primaryPhone ? (
                            <a
                                href={`tel:${normalizePhoneForLink(
                                    primaryPhone.value
                                )}`}
                                style={styles.actionButton}
                            >
                                <i
                                    className="fa fa-phone"
                                    style={styles.actionIcon}
                                />
                                Llamar
                            </a>
                        ) : null}

                        {primaryEmail ? (
                            <a
                                href={`mailto:${primaryEmail.value}`}
                                style={styles.actionButton}
                            >
                                <i
                                    className="fa fa-envelope-o"
                                    style={styles.actionIcon}
                                />
                                Correo
                            </a>
                        ) : null}

                        {primaryPhone ? (
                            <a
                                href={
                                    `https://wa.me/${
                                        normalizePhoneForWhatsApp(
                                            primaryPhone.value
                                        )
                                    }`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                style={styles.actionButton}
                            >
                                <i
                                    className="fa fa-whatsapp"
                                    style={styles.actionIcon}
                                />
                                WhatsApp
                            </a>
                        ) : null}
                    </div>

                    <div style={styles.contactSection}>
                        {phones.map(contact => (
                            <ContactRow
                                key={`phone-${contact.value}`}
                                icon="fa fa-phone"
                                label={contact.label}
                                value={contact.value}
                                href={`tel:${normalizePhoneForLink(
                                    contact.value
                                )}`}
                            />
                        ))}

                        {emails.map(contact => (
                            <ContactRow
                                key={`email-${contact.value}`}
                                icon="fa fa-envelope-o"
                                label={contact.label}
                                value={contact.value}
                                href={`mailto:${contact.value}`}
                            />
                        ))}
                    </div>

                    {socialLinks.length ? (
                        <div style={styles.socialSection}>
                            {socialLinks.map(link => (
                                <a
                                    key={link.key}
                                    href={link.value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={link.label}
                                    style={styles.socialButton}
                                >
                                    <i className={link.icon} />
                                </a>
                            ))}
                        </div>
                    ) : null}

                    <button
                        type="button"
                        onClick={downloadVCard}
                        disabled={downloading}
                        style={styles.saveButton}
                    >
                        <i
                            className={
                                downloading
                                    ? 'fa fa-spinner fa-spin'
                                    : 'fa fa-address-book-o'
                            }
                            style={{marginRight: '9px'}}
                        />

                        {downloading
                            ? 'Preparando contacto...'
                            : 'Guardar contacto'}
                    </button>
                </div>

                <div style={styles.footer}>
                    TARJETA DIGITAL CORPORATIVA
                </div>
            </div>
        </div>
    );
};

const ContactRow = ({
    icon,
    label,
    value,
    href
}) => (
    <a href={href} style={styles.contactRow}>
        <div style={styles.contactIcon}>
            <i className={icon} />
        </div>

        <div style={{minWidth: 0}}>
            <div style={styles.contactLabel}>
                {label}
            </div>

            <div style={styles.contactValue}>
                {value}
            </div>
        </div>
    </a>
);

const PublicCardStatus = ({
    icon,
    title,
    message
}) => (
    <div style={styles.page}>
        <div style={styles.statusCard}>
            <i
                className={icon}
                style={{
                    fontSize: '46px',
                    color: '#3c6f98'
                }}
            />

            <h2>{title}</h2>

            <p style={{color: '#687586'}}>
                {message}
            </p>
        </div>
    </div>
);

const styles = {
    page: {
        minHeight: '100vh',
        padding: '28px 15px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background:
            'linear-gradient(145deg, #eaf0f5 0%, #f8fafc 100%)',
        fontFamily: 'Arial, sans-serif'
    },
    card: {
        width: '100%',
        maxWidth: '430px',
        overflow: 'hidden',
        borderRadius: '22px',
        background: '#ffffff',
        boxShadow: '0 20px 55px rgba(23, 45, 80, 0.20)'
    },
    heroHeader: {
        position: 'relative',
        marginBottom: '0'
    },

    heroBackground: {
        height: '190px',
        padding: '18px 22px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        backgroundImage:
            `linear-gradient(
                rgba(20,40,72,.18),
                rgba(20,40,72,.72)
            ),
            url(${loungeBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflow: 'hidden'
    },

    heroLogo: {
        width: '95px',
        marginBottom: '0',
        transform: 'translateY(10px)',
        filter:
            'drop-shadow(0 5px 12px rgba(0,0,0,.35))'
    },

    identitySection: {
        position: 'relative',
        left: 0,
        right: 0,
        marginTop: '-106px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'center',
        padding: '0 24px'
    },

    heroPhoto: {
        width: '118px',
        height: '118px',
        marginLeft: '8px',
        alignSelf: 'flex-start',
        objectFit: 'cover',
        borderRadius: '50%',
        border: '5px solid #fff',
        background: '#fff',
        boxShadow:
            '0 10px 30px rgba(0,0,0,.30)'
    },
    name: {
        margin:'18px 0 4px',
        fontSize:'21px',
        lineHeight: 1.2,
        fontWeight: 700
    },
    position: {
        margin: 0,
        color:'#2d5d8a',
        fontSize: '14px',
        fontWeight: 600
    },
    area: {
        margin: '5px 0 0',
        fontSize: '11px',
        letterSpacing: '0.7px',
        opacity:.70
    },
    company: {
        margin: '15px 0 0',
        fontSize: '12px',
        opacity:.82
    },
    body: {
        padding: '22px 24px 25px'
    },
    primaryActions: {
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '20px'
    },
    actionButton: {
        flex: 1,
        minHeight: '62px',
        padding: '10px 6px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '12px',
        color: '#28527e',
        background: '#eef5fa',
        textAlign: 'center',
        textDecoration: 'none',
        fontSize: '12px',
        fontWeight: 600
    },
    actionIcon: {
        display: 'block',
        margin: '0 0 6px',
        fontSize: '18px',
        lineHeight: 1
    },
    contactSection: {
        borderTop: '1px solid #edf0f4'
    },
    contactRow: {
        padding: '13px 2px',
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #edf0f4',
        color: '#253a50',
        textDecoration: 'none'
    },
    contactIcon: {
        width: '38px',
        height: '38px',
        minWidth: '38px',
        marginRight: '12px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '11px',
        color: '#337ab7',
        background: '#eef5fa'
    },
    contactLabel: {
        marginBottom: '3px',
        color: '#8a96a3',
        fontSize: '10px',
        textTransform: 'uppercase'
    },
    contactValue: {
        overflow: 'hidden',
        fontSize: '13px',
        textOverflow: 'ellipsis'
    },
    socialSection: {
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        margin: '22px 0'
    },
    socialButton: {
        width: '40px',
        height: '40px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        color: '#ffffff',
        background: '#28527e',
        textDecoration: 'none',
        fontSize: '17px'
    },
    saveButton: {
        width: '100%',
        padding: '13px',
        border: 0,
        borderRadius: '13px',
        color: '#ffffff',
        background: '#28527e',
        fontWeight: 600
    },
    footer: {
        padding: '13px',
        color: '#778494',
        background: '#f6f8fa',
        textAlign: 'center',
        fontSize: '9px',
        letterSpacing: '1px'
    },
    statusCard: {
        width: '100%',
        maxWidth: '420px',
        padding: '55px 30px',
        borderRadius: '20px',
        background: '#ffffff',
        textAlign: 'center',
        boxShadow: '0 18px 45px rgba(23,45,80,0.15)'
    }
};

export default PublicCardPage;
